import { Switch } from "@headlessui/react";
import { Popover } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { POUND_SIGN } from "@/Constants/common";
import { toggleBankPublishContractDurationModal } from "@/reducers/clientcommon";
import { updateJobData } from "@/reducers/clientcreatejob";
import { showRoleAccessToast } from "@/utilities/utils";

import ComplianceInfoIcon from "../../../Icons/ComplianceInfoIcon";

/**
 * Renders the summary component with job details and related information.
 *
 * @return {JSX.Element} The summary component with job details and related information.
 */
export const Summary = () => {
  const dispatch = useDispatch();

  const { jobData, candidateRoleList, skillsList, jobShiftObj } = useSelector(
    (state) => state.clientCreateJob,
  );
  const { oldBankContractPublishHours } = useSelector(
    (state) => state.clientCommon,
  );
  const { user } = useSelector((state) => state.clientProfile);
  const { isPremiumAccount, clientUserAllPermissions } = useSelector(
    (state) => state.session,
  );

  const [skills, setSkills] = useState([]);

  const days = Math.floor(oldBankContractPublishHours / 24);
  const hours = oldBankContractPublishHours % 24;

  const getRole = () =>
    candidateRoleList.find((item) => item.id === jobData.roleId)?.name || "-";

  const getSkillsList = () =>
    jobData.skills.map(
      (id) => skillsList.find((skill) => skill.id === id)?.name,
    );

  useEffect(() => {
    setSkills(getSkillsList());
  }, [jobData.skills, skillsList]);

  const TableRow = ({
    label,
    value,
    isInteger = false,
    highlight = false,
    showCurrency = true,
  }) => (
    <tr
      className={`border-b border-b-lightblue ${highlight ? "bg-[#e1f5e1]" : ""}`}
    >
      <th
        className={`p-2 text-left font-medium ${highlight ? "font-semibold text-green" : ""}`}
        width="150px"
      >
        {label}
      </th>
      <td
        className={`p-2 text-[14px] font-medium leading-[normal] ${highlight ? "font-semibold text-green" : ""}`}
      >
        {showCurrency
          ? `${POUND_SIGN} ${isInteger ? Number(value).toFixed(0) : Number(value).toFixed(2)}`
          : Number(value).toFixed(0)}
      </td>
    </tr>
  );

  const renderVisitPayBreakdown = () => {
    const {
      priceOfVisitPayCharges = 0,
      noOfVisit = 0,
      totalDays = 0,
    } = jobData;
    const avgRateArr = jobShiftObj?.map(
      (shift) =>
        shift.rate_values.reduce((acc, rate) => acc + (rate.rate || 0), 0) /
          shift.rate_values.length || 0,
    );
    const totalRates = avgRateArr?.reduce((acc, rate) => acc + rate, 0) || 0;
    const totalAvg = totalRates / avgRateArr?.length || 0;
    const candidateVisitPay = (totalAvg * priceOfVisitPayCharges) / 100 || 0;
    const totalAmount = candidateVisitPay * noOfVisit * totalDays || 0;

    return (
      <>
        <TableRow label="Pay Per Visit" value={candidateVisitPay} />
        <TableRow
          label="No Of Visit"
          value={noOfVisit}
          isInteger
          showCurrency={false}
        />
        <TableRow label="No Of Days" value={totalDays} showCurrency={false} />
        <TableRow label="Total" value={totalAmount} highlight />
      </>
    );
  };

  return (
    <div className="Summary_Wrapper">
      <div className="flex flex-col gap-4 rounded-lg bg-white p-4">
        <h4 className="text-base font-bold leading-[normal] text-black">
          Summary
        </h4>
        <div className="grid gap-3">
          {isPremiumAccount && user.is_bank_client ? (
            <div className="grid-cols grid gap-4 rounded-lg border border-lightblue p-3">
              <div className="grid gap-2">
                <h5 className="whitespace-break-spaces break-words text-sm font-semibold text-txtDark">
                  Publish to AppLocum Candidates
                </h5>
                <div className="flex items-center gap-2">
                  <span className="w-52 truncate whitespace-break-spaces break-words text-xs font-medium text-txtColor">
                    Choose whether to publish this contract with the wider group
                    of AppLocum candidates?
                  </span>
                  <Switch
                    checked={jobData?.is_for_appLocum_candidates}
                    onChange={() =>
                      dispatch(
                        updateJobData({
                          is_for_appLocum_candidates:
                            !jobData?.is_for_appLocum_candidates,
                        }),
                      )
                    }
                    className={`group inline-flex !h-[1.77rem] !w-[3.025rem] items-center rounded-full shadow-sm transition-colors duration-200 ${jobData?.is_for_appLocum_candidates ? "bg-primary" : "bg-[#dddddd]"} `}
                  >
                    <span
                      className={`inline-block h-[22px] w-[22px] transform rounded-full bg-white shadow-md transition-transform duration-300 ${
                        jobData?.is_for_appLocum_candidates
                          ? "translate-x-[23px]"
                          : "translate-x-1"
                      }`}
                    />
                  </Switch>
                </div>
              </div>
              {jobData?.is_for_appLocum_candidates ? (
                <>
                  <div className="flex">
                    <span className="truncate whitespace-break-spaces break-words text-xs font-medium text-txtColor">
                      The contract will be available to <b>Bank candidates</b>{" "}
                      for the initial{" "}
                      <b>
                        {days > 0 ? `${days} day${days > 1 ? "s" : ""}` : ""}
                        &nbsp;
                        {hours > 0
                          ? `${hours} hour${hours > 1 ? "s" : ""}`
                          : ""}
                      </b>
                      , and then it will be published with the wider group of
                      AppLocum candidates.
                    </span>
                  </div>
                  <div className="flex justify-end">
                    <button
                      className={`item-end flex w-fit rounded-md border border-blue bg-white px-2 py-1 text-xs font-medium text-blue transition-all duration-200 ease-linear ${
                        !clientUserAllPermissions ||
                        clientUserAllPermissions.is_allow_to_change_bank_management_setting
                          ? "hover:bg-blue hover:text-white"
                          : "opacity-50"
                      }`}
                      onClick={() => {
                        if (
                          !clientUserAllPermissions ||
                          clientUserAllPermissions.is_allow_to_change_bank_management_setting
                        ) {
                          dispatch(
                            toggleBankPublishContractDurationModal(true),
                          );
                        } else {
                          showRoleAccessToast();
                        }
                      }}
                    >
                      Change duration
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          ) : null}

          <div className="grid grid-cols-2">
            <span className="truncate text-xs font-medium text-txtColor">
              Title of Job
            </span>
            <span className="truncate text-xs font-bold text-black">
              {jobData.jobTitle}
            </span>
          </div>
          <div className="grid grid-cols-2">
            <span className="truncate text-xs font-medium text-txtColor">
              Role
            </span>
            <span className="truncate text-xs font-bold text-black">
              {getRole()}
            </span>
          </div>
          {skills.length > 0 ? (
            <div className="grid grid-cols-2">
              <span className="truncate text-xs font-medium text-txtColor">
                Skills
              </span>
              <div className="flex flex-wrap gap-[6px] text-[11px] font-normal text-black">
                {skills.map((item, index) => (
                  <span
                    className="inline-block rounded bg-lightblue p-[3px_4px] text-primary"
                    key={index}
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          {jobData.totalDays > 0 && (
            <div className="grid grid-cols-2">
              <span className="truncate text-xs font-medium text-txtColor">
                No. of days
              </span>
              <span className="truncate text-xs font-bold text-black">
                {jobData.totalDays}
              </span>
            </div>
          )}
          {jobData.totalHours > 0 && !jobData?.dayRate && (
            <div className="grid grid-cols-2">
              <span className="truncate text-xs font-medium text-txtColor">
                No. of total hours
              </span>
              <span className="truncate text-xs font-bold text-black">
                {Number(jobData.totalHours).toFixed(2)}
              </span>
            </div>
          )}
          <div className="grid gap-1 border-t border-t-lightblue pt-3">
            <div className="grid grid-cols-2">
              <span className="truncate text-xs font-medium text-txtColor">
                Total pay to candidate
              </span>
              <span className="truncate text-sm font-bold text-green">
                {POUND_SIGN} {Number(jobData.totalCandidatePay).toFixed(2)}
              </span>
            </div>
            {jobData.startDate &&
              jobData.endDate &&
              jobData.startTime &&
              jobData.endTime &&
              jobData.noOfVisit > 0 && (
                <div className="grid grid-cols-2">
                  <div className="flex items-center gap-1">
                    <span className="truncate text-xs font-medium text-txtColor">
                      Visit pay
                    </span>
                    <Popover
                      rootClassName="z-[1033] ClientPayPopover_Wrapper"
                      placement="topRight"
                      content={
                        <table className="!rounded-lg">
                          <thead className="!rounded-lg bg-lightblue">
                            <tr>
                              <th
                                className="p-2 text-left font-medium text-primary"
                                width="160px"
                              >
                                Info
                              </th>
                              <th
                                className="p-2 text-left font-medium text-primary"
                                width="95px"
                              >
                                Value
                              </th>
                            </tr>
                          </thead>
                          <tbody>{renderVisitPayBreakdown()}</tbody>
                        </table>
                      }
                    >
                      <span>
                        <ComplianceInfoIcon cls="w-[18px] h-[18px] cursor-pointer text-txtColor mt-[2px]" />
                      </span>
                    </Popover>
                  </div>
                  <span className="truncate text-sm font-bold text-green">
                    {POUND_SIGN}{" "}
                    {Number(jobData?.totalClientChargesPerVisit).toFixed(2)}
                  </span>
                </div>
              )}
            <div className="grid grid-cols-2">
              <span className="text-xs font-medium text-txtColor">
                Applocum service fee <br />
                for contract
              </span>
              <span className="truncate text-sm font-bold text-green">
                {POUND_SIGN}{" "}
                {(jobData.applocumServiceFee &&
                  parseFloat(jobData.applocumServiceFee).toFixed(2)) ||
                  "0.00"}
              </span>
            </div>
          </div>
          <div className="border-t border-t-lightblue pt-3">
            <div className="grid grid-cols-2">
              <span className="text-base font-semibold text-txtColor">
                Total Charge
              </span>
              <span className="truncate text-base font-bold text-green">
                {POUND_SIGN}{" "}
                {(jobData.totalCost &&
                  parseFloat(jobData.totalCost).toFixed(2)) ||
                  "0.00"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
